import { useEffect, useRef, useState } from "react";
import { X, MapPin } from "lucide-react";
import toast from "react-hot-toast";
import { getStorageItem } from "../../utils/sessionStorage";
import { useUserStore } from "../../store/userStore";
import AddressPopup from "./addressPopup";
import { useAppointmentBookingStore } from "../../store/appointmentPayloadStore";
import { useNavigate } from "react-router-dom";
import { API } from "../../utils/axios";
import { useMetaStore } from "../../store/data";
import { Commission } from "../../components/interfaces";

interface BookingPopupProps {
  isOpen: boolean;
  onClose: () => void;
  merchant: any;
  selectedPractitioner: any;
}

export default function BookingPopup({
  isOpen,
  onClose,
  merchant,
  selectedPractitioner,
}: BookingPopupProps) {
  const { user, updateUser } = useUserStore();
  const { commissionData } = useMetaStore();
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [openAddress, setOpenAddress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [cardProcessingFee, setCardProcessingFee] = useState(0);
  const [tax, setTax] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [issue, setIssue] = useState("");
  const [consentCheck, setConsentCheck] = useState(false);
  const [aboveEighteen, setAboveEighteen] = useState(false);
  const { updateBookingPayload } = useAppointmentBookingStore();
  const navigate = useNavigate();
  const [addressLoading, setAddressLoading] = useState(false);
  const commissionRef = useRef(0);
  const currentBookingTime = new Date()

  const handleSave = async (data: any) => {
    try {
      setAddressLoading(true);
      const response = await API.post("/user/address", data);

      const address = response?.data?.data;
      updateUser({
        city: address?.city,
        state: address?.custom_field,
        address: address?.address_1,
        zipcode: address?.postcode,
      });
      toast.success(response?.data?.message);
      setOpenAddress(false);
    } catch (err) {
      console.error("Error saving address:", err);
      toast.error("Failed to save address");
    } finally {
      setAddressLoading(false);
    }
  };

  const servicesObject: Record<string, string> = {
    "1": "Physiotherapy",
    "2": "Chiropractic",
    "3": "Acupuncture",
    "4": "Massage Therapy",
  };

  function getCommission(servicetypeId: number, businessType: string) {
    const keyMap: Record<string, keyof Commission> = {
      "Clinic Based Practice": "clinic_based_tax",
      "Home Based Practice": "home_based_tax",
      "Mobile Practice": "mobile_based_tax",
    };

    const commission = commissionData.find(
      (item: any) => item.servicetype_id === servicetypeId,
    );

    return commission?.[keyMap[businessType]] ?? 0;
  }

  const handleConfirmBooking = async () => {
    if (!user?.id) {
      toast.error("Please Login first");
      return;
    }
    if (!selectedSlot) {
      toast.error("Please select a timeslot");
      return;
    }
    if (!consentCheck) {
      toast.error("Please select consent box before booking the appointment");
      return;
    }
    if (!aboveEighteen) {
      toast.error("Please select consent box before booking the appointment");
      return;
    }
    if (
      merchant.businessType === "Mobile Practice" &&
      (!user.city || !user.address || !user.state || !user.zipcode)
    ) {
      toast.error("Please fill required address fields");
      setOpenAddress(true);
      return;
    }

    commissionRef.current = getCommission(
      selectedSlot?.servicetype_id,
      merchant?.businessType,
    );

    setLoading(true);
    try {
      const bookAppointmentPayload = {
        practitionerEmail: selectedPractitioner?.practitionerEmail,
        practitionerName: selectedPractitioner?.practitionerName,
        serviceName: servicesObject[selectedSlot?.servicetype_id?.toString()],
        businessId: merchant?.id,
        practitionerId: selectedPractitioner?.id,
        serviceTypeId: selectedSlot?.servicetype_id,
        slotId: selectedSlot?.schedulelist_id,
        bookingDate: selectedSlot?.schedule_date,
        bookingStartTime: selectedSlot?.slot_start,
        bookingEndTime: selectedSlot?.slot_end,
        appointmentDate: selectedSlot?.slot_start,
        amount: totalAmount,
        baseAmount: amount,
        taxPercentage: taxPercentage,
        serviceTax: tax,
        cardFee: cardProcessingFee,
        currency: "cad",
        notes: issue,
        commission: commissionRef.current,
        expire_at: currentBookingTime.toISOString()
      };
      updateBookingPayload(bookAppointmentPayload);
      navigate("/save-card");
    } catch (error: any) {
      console.error("Error in saving card:", error);
      toast.error(error.response?.data?.message || "Failed to save card");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedSlot) return;
    const serviceType = servicesObject[selectedSlot.servicetype_id];
    setAmount(selectedSlot?.amount);
    let TAX_RATES;
    if (serviceType === "Massage Therapy") {
      TAX_RATES = merchant?.serviceTax.message;
    } else {
      TAX_RATES = merchant?.serviceTax?.[serviceType.toLowerCase()];
    }
    setTaxPercentage(TAX_RATES);

    const calculatedTax = (amount * TAX_RATES) / 100;
    setTax(Number(calculatedTax.toFixed(2)));

    const fee = ((amount) * 1.3) / 100;
    setCardProcessingFee(Number(fee.toFixed(2)));

    const total = amount + calculatedTax + fee;
    setTotalAmount(Number(total.toFixed(2)));
  }, [amount, user?.stateId, selectedSlot]);

  if (!isOpen || !selectedPractitioner) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-[95%] md:w-[850px] max-h-[90vh] overflow-y-auto relative animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-6 border-b-2 border-gray-100 mt-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-gray-900">
                {merchant?.businessName || "Business Name"}
              </h2>
              <div className="flex items-center text-gray-600 mt-1 text-sm">
                <MapPin className="w-4 h-4 mr-1 text-gray-500" />
                <span>
                  {merchant?.merchantAddress || "Address not available"}
                </span>
              </div>
              <p className="mt-3 text-gray-600 leading-relaxed text-sm md:text-base">
                {merchant?.business_desc}
              </p>
            </div>
            {merchant?.businessPhotos ? (
              <img
                src={merchant.businessPhotos}
                alt={merchant?.businessName}
                className="w-24 h-24 object-cover rounded-xl border border-gray-200 shadow-sm"
              />
            ) : (
              <div className="h-24 w-24 rounded-xl ring-2 ring-white shadow-md bg-stone-200 flex items-center justify-center text-center">
                <span className="text-[10px] font-medium text-stone-600 leading-tight">
                  No image
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Practitioner Section */}
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            {selectedPractitioner?.profilePicture ? (
              <img
                src={selectedPractitioner?.profilePicture}
                alt={selectedPractitioner?.practitionerName}
                className="w-16 h-16 rounded-2xl object-cover border border-gray-200 shadow-md"
              />
            ) : (
              <div className="h-16 w-16 rounded-2xl ring-2 ring-white shadow-md bg-stone-200 flex items-center justify-center text-center">
                <span className="text-[10px] font-medium text-stone-600 leading-tight">
                  No image
                </span>
              </div>
            )}
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {selectedPractitioner?.practitionerName}
              </h3>
            </div>
          </div>

          {merchant.businessType === "Mobile Practice" && (
            <div className="my-6">
              <button
                onClick={() => {
                  if (!user?.id)
                    return toast.error("Please login first");
                  setOpenAddress(true);
                }}
                className="bg-violet-600 hover:bg-violet-700 text-white text-base font-normal px-4 py-1 rounded-lg"
              >
                Add Address
              </button>

              <AddressPopup
                isOpen={openAddress}
                onClose={() => setOpenAddress(false)}
                onSubmit={handleSave}
                addressLoading={addressLoading}
              />
            </div>
          )}

          {/* Slots */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Available Slots:
            </p>

            {selectedPractitioner?.availabilities?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {selectedPractitioner?.availabilities.map(
                  (slot: any, i: number) => {
                    const serviceName =
                      servicesObject[slot.servicetype_id?.toString()] ||
                      "Unknown Service";

                    const options: Intl.DateTimeFormatOptions = {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                      timeZone: "UTC",
                    };

                    const availableSlotStarttime = new Date(
                      slot.slot_start,
                    ).toLocaleString(undefined, options);
                    return (
                      <div
                        key={i}
                        className={`h-[80px] flex flex-col items-center justify-center text-center rounded-xl border ${
                          selectedSlot?.schedulelist_id === slot.schedulelist_id
                            ? "bg-teal-200"
                            : "bg-teal-50"
                        } border-teal-200 shadow-sm hover:bg-teal-100 hover:shadow-md transition-all cursor-pointer group`}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        <span className="text-gray-600 text-[11px] mt-1 font-medium truncate w-[80px]">
                          {serviceName}
                        </span>
                        <span className="text-teal-800 text-sm font-semibold tracking-wide">
                          {availableSlotStarttime}
                        </span>
                      </div>
                    );
                  },
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No slots available</p>
            )}
          </div>

          {selectedPractitioner?.availabilities.length > 0 && (
            <div>
              <div className="p-4 bg-white rounded-xl shadow-md space-y-3 text-gray-800 text-sm sm:text-base mt-4">
                {/* Subtotal */}
                <div className="flex justify-between">
                  <span className="font-medium">Subtotal:</span>
                  <span>${amount}</span>
                </div>

                {/* Processing Fee */}
                <div className="flex justify-between">
                  <span className="font-medium">
                    Platform Fee (1.3%):
                  </span>
                  <span>${cardProcessingFee}</span>
                </div>

                {/* Taxes */}
                <div className="flex justify-between">
                  <span className="font-medium">Taxes ({taxPercentage}%):</span>
                  <span>${tax}</span>
                </div>

                {/* Divider */}
                <div className="border-t pt-3" />

                {/* Grand Total */}
                <div className="flex justify-between text-lg font-semibold text-blue-700">
                  <span>Total Amount:</span>
                  <span>${totalAmount}</span>
                </div>
              </div>

              <div className="w-full max-w-md my-6">
                <label
                  htmlFor="issue"
                  className="block mb-1 text-md font-medium text-gray-700"
                >
                  Reason for Visit
                </label>

                <textarea
                  id="issue"
                  name="issue"
                  value={issue}
                  onChange={(e) => setIssue(e.target.value)}
                  placeholder="Briefly describe your concern (e.g., previous medical issue, follow-up, skin rash)"
                  rows={3}
                  maxLength={200}
                  className="
          w-full
          resize-none
          rounded-lg
          border
          border-gray-300
          bg-white
          px-3
          py-2
          text-sm
          text-gray-900
          placeholder-gray-400
          shadow-sm
          transition
          focus:border-blue-600
          focus:ring-2
          focus:ring-blue-200
          focus:outline-none
        "
                />

                <p className="mt-1 text-xs text-gray-500 mb-6">
                  Keep it short (max 200 characters)
                </p>
              </div>

              <div>
                <label>
                  <input
                    type="checkbox"
                    checked={consentCheck}
                    className="my-1 mr-2"
                    onChange={(e) => setConsentCheck(e.target.checked)}
                    required
                  />
                  I agree to receive updates and communications via SMS and
                  email.
                </label>
              </div>

              <label>
                <input
                  type="checkbox"
                  checked={aboveEighteen}
                  className="my-1 mr-2"
                  onChange={(e) => setAboveEighteen(e.target.checked)}
                  required
                />
                I am above 18 years of age.
              </label>

              <div className="flex justify-end my-6">
                <button
                  onClick={handleConfirmBooking}
                  disabled={loading}
                  className="bg-violet-600 hover:bg-violet-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Saving..." : "Continue to payment"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
